<template>
  <div class="dialog-wrap">
    <el-dialog title="批量添加用户"
               :close-on-click-modal="false"
               :close-on-press-escape="!submitting"
               :show-close="!submitting"
               :visible.sync="dialogVisible"
               @close="handleClose"
               width="560px">
      <el-form class="form"
               :model="dataForm"
               :rules="rules"
               ref="dataForm"
               label-width="100px">
        <el-form-item label="手机号"
                      prop="mobiles">
          <el-input v-model="dataForm.mobiles"
                    type="textarea"
                    :disabled="submitting"
                    :rows="9"
                    placeholder="每行输入一个手机号，最多200个"></el-input>
          <div class="mobile-tip"
               :class="{ 'is-error': mobileCount > 200 }">
            已输入 {{ mobileCount }}/200 个手机号，空行将自动忽略
          </div>
        </el-form-item>
        <el-form-item label="渠道邀请人"
                      prop="crm_inviter_id">
          <el-select v-model="dataForm.crm_inviter_id"
                     :disabled="submitting"
                     :loading="inviterLoading"
                     :remote-method="handleInviterSearch"
                     clearable
                     filterable
                     remote
                     reserve-keyword
                     placeholder="输入用户名/机构名/手机号搜索"
                     style="width: 100%">
            <el-option v-for="item in inviterOptions"
                       :key="item.id"
                       :label="formatInviterOption(item)"
                       :value="item.id">
            </el-option>
          </el-select>
          <div class="inviter-tip">选填；不选择时，创建的账号不关联CRM邀请人</div>
        </el-form-item>
      </el-form>
      <div slot="footer"
           class="dialog-footer">
        <el-button :disabled="submitting"
                   @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary"
                   :loading="submitting"
                   @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { crmCompanyUserList, memberBulkCreate } from '@/api/member'

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogVisible: this.visible,
      dataForm: {
        mobiles: '',
        crm_inviter_id: null,
      },
      rules: {
        mobiles: [{ validator: this.validateMobiles, trigger: 'blur' }],
      },
      inviterOptions: [],
      inviterLoading: false,
      inviterSearchTimer: null,
      inviterRequestId: 0,
      submitting: false,
    }
  },
  computed: {
    mobileEntries() {
      return String(this.dataForm.mobiles || '')
        .split(/\r?\n/)
        .map((mobile, index) => ({
          mobile: mobile.trim(),
          line: index + 1,
        }))
        .filter((item) => item.mobile)
    },
    mobileCount() {
      return this.mobileEntries.length
    },
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.fetchInviterOptions('')
      }
    },
  },
  beforeDestroy() {
    this.clearInviterSearchTimer()
  },
  methods: {
    validateMobiles(rule, value, callback) {
      const entries = this.mobileEntries
      if (!entries.length) {
        callback(new Error('请输入手机号'))
        return
      }
      if (entries.length > 200) {
        callback(new Error('单次最多可批量添加200个用户'))
        return
      }

      const invalidEntries = entries.filter(
        (item) => !/^1[3-9]\d{9}$/.test(item.mobile)
      )
      if (invalidEntries.length) {
        const lines = invalidEntries
          .slice(0, 5)
          .map((item) => item.line)
          .join('、')
        const suffix = invalidEntries.length > 5 ? '等' : ''
        callback(new Error(`第${lines}${suffix}行手机号格式不正确`))
        return
      }

      const mobileLines = {}
      entries.forEach((item) => {
        if (!mobileLines[item.mobile]) {
          mobileLines[item.mobile] = []
        }
        mobileLines[item.mobile].push(item.line)
      })
      const duplicateMobile = Object.keys(mobileLines).find(
        (mobile) => mobileLines[mobile].length > 1
      )
      if (duplicateMobile) {
        callback(
          new Error(
            `手机号${duplicateMobile}在第${mobileLines[duplicateMobile].join(
              '、'
            )}行重复`
          )
        )
        return
      }

      callback()
    },
    handleInviterSearch(keyword) {
      this.clearInviterSearchTimer()
      this.inviterSearchTimer = setTimeout(() => {
        this.fetchInviterOptions(keyword)
      }, 300)
    },
    fetchInviterOptions(keyword) {
      const requestId = ++this.inviterRequestId
      this.inviterLoading = true
      crmCompanyUserList({ keyword: String(keyword || '').trim() })
        .then((res) => {
          if (requestId !== this.inviterRequestId) {
            return
          }
          const list = res && Array.isArray(res.list) ? res.list : []
          const selected = this.inviterOptions.find(
            (item) => item.id === this.dataForm.crm_inviter_id
          )
          if (selected && !list.some((item) => item.id === selected.id)) {
            this.inviterOptions = [selected].concat(list)
          } else {
            this.inviterOptions = list
          }
        })
        .catch(() => {})
        .finally(() => {
          if (requestId === this.inviterRequestId) {
            this.inviterLoading = false
          }
        })
    },
    formatInviterOption(item) {
      const parts = [item.company_name, item.alias, item.phone].filter(
        (value) =>
          value !== null &&
          value !== undefined &&
          String(value).trim() !== ''
      )
      return parts.length ? parts.join('/') : `机构用户 #${item.id}`
    },
    handleSubmit() {
      if (this.submitting) {
        return
      }
      this.$refs.dataForm.validate((valid) => {
        if (!valid) {
          return
        }

        const params = {
          arr_mobile: this.mobileEntries.map((item) => item.mobile),
        }
        if (
          this.dataForm.crm_inviter_id !== null &&
          this.dataForm.crm_inviter_id !== undefined &&
          this.dataForm.crm_inviter_id !== ''
        ) {
          params.crm_inviter_id = this.dataForm.crm_inviter_id
        }

        this.submitting = true
        memberBulkCreate(params)
          .then((res) => {
            if (typeof res === 'string') {
              return
            }
            this.$message.success('批量添加成功')
            this.$emit('submit')
            this.dialogVisible = false
          })
          .catch(() => {})
          .finally(() => {
            this.submitting = false
          })
      })
    },
    clearInviterSearchTimer() {
      if (this.inviterSearchTimer) {
        clearTimeout(this.inviterSearchTimer)
        this.inviterSearchTimer = null
      }
    },
    resetForm() {
      this.clearInviterSearchTimer()
      this.inviterRequestId += 1
      this.inviterLoading = false
      this.inviterOptions = []
      this.dataForm.mobiles = ''
      this.dataForm.crm_inviter_id = null
      this.$nextTick(() => {
        if (this.$refs.dataForm) {
          this.$refs.dataForm.clearValidate()
        }
      })
    },
    handleClose() {
      this.resetForm()
      this.$emit('close')
    },
  },
}
</script>

<style lang="less" scoped>
.dialog-wrap {
  .form {
    margin-bottom: -18px;
  }
  .mobile-tip,
  .inviter-tip {
    color: #909399;
    font-size: 12px;
    line-height: 20px;
  }
  .mobile-tip.is-error {
    color: #f56c6c;
  }
}
</style>
